import { useEffect, useMemo, lazy, Suspense } from 'react'
import styled from 'styled-components'
import { Route, Routes, Navigate } from 'react-router-dom'
import _ from 'underscore'
import Honeybadger from 'honeybadger-js'

import AppLayout from '../layouts/AppLayout'

import Spinner from 'components/Spinner'
import ZendeskWidget from 'components/ZendeskWidget'

import Brand from 'helpers/brand'
import BrowserCompatibility from 'helpers/browserCompatibility'
import GA4 from 'helpers/ga4'
import WindowLocation from 'helpers/windowLocation'
import Apptentive from 'helpers/apptentive'

import NexiaStream from 'services/NexiaStream'
import Api from 'services/api'

import useSearchParams from 'hooks/useSearchParams'
import { useServerErrorContext } from 'hooks/ServerErrorContext'
import { useSessionContext } from 'hooks/SessionContext'
import { StoreContextProvider } from 'hooks/StoreContext'
import { FeaturesContextProvider } from 'hooks/FeaturesContext'

import AmplifyLoginHelper from 'helpers/AmplifyLoginHelper'

import { useAlertDialog } from 'hooks/useDialog'

const AdminRouter = lazy(() => import('routers/AdminRouter'))
const AlertsRouter = lazy(() => import('routers/AlertsRouter'))
const CustomersRouter = lazy(() => import('routers/CustomersRouter'))
const FavoritesRouter = lazy(() => import('routers/FavoritesRouter'))
const DevicesRouter = lazy(() => import('routers/DevicesRouter'))
const NotificationsRouter = lazy(() => import('routers/NotificationsRouter'))
const PaywallRouter = lazy(() => import('routers/PaywallRouter'))
const HEHMRouter = lazy(() => import('routers/HEHMRouter'))

const DeviceAggregator = lazy(() => import('pages/DeviceAggregator'))
const ArchivedDevices = lazy(() => import('pages/ArchivedDevices'))
const Dashboard = lazy(() => import('pages/Dashboard'))
const EmailSubscriptionPreferences = lazy(() => import('pages/EmailSubscriptionPreferences'))
const VerifyNotificationRecipient = lazy(() => import('pages/Notifications/VerifyNotificationRecipient'))
const UnsubscribeEmail = lazy(() => import('pages/UnsubscribeEmail'))
const Resources = lazy(() => import('pages/Resources'))
const Legal = lazy(() => import('pages/Legal'))
const SiteRebranded = lazy(() => import('pages/SiteRebranded'))
const TermsAndConditions = lazy(() => import('pages/TermsAndConditions'))
const TermsAndConditionsForm = lazy(() => import('pages/TermsAndConditions/TermsAndConditionsForm'))
const UnassignedDevice = lazy(() => import('pages/UnassignedDevice'))
const UnassignedDevices = lazy(() => import('pages/UnassignedDevices'))
const Updates = lazy(() => import('pages/Updates'))
const YouDontHavePermission = lazy(() => import('pages/YouDontHavePermission'))
const WarrantyLookup = lazy(() => import('pages/WarrantyLookup'))
const DealerImage = lazy(() => import('pages/DealerImage'))
const TechAssistantChatbot = lazy(() => import('pages/TechAssistant'))
const DailyDigest = lazy(() => import('pages/DailyDigest'))
const NotFound = lazy(() => import('pages/NotFound'))

const DEFAULT_PAGE = {
  fsr: '/devices',
  admin: '/devices',
  technician: '/devices',
  utility: '/devices',
  dealer: '/dashboard',
  dso: '/customers',
  iwd: '/customers'
}

const AppRouter = () => {
  const { session, isSessionLoaded } = useSessionContext()
  const { setServerError } = useServerErrorContext()

  const redirectCalled = useSearchParams('redirectedToNewBrand')

  const { alert } = useAlertDialog()

  useEffect(() => {
    const compatible = new BrowserCompatibility()
    if (!compatible.isCompatible(navigator.userAgent)) {
      alert({
        description: 'Your browser is not supported or is in compatibility mode.\n\n' +
        'Please change your browser to use one of the following supported browsers IE 10+, Chrome, Firefox or Safari. ' +
        'IE10 compatibility mode is not supported.'
      })
    }

    Brand.initialize()

    NexiaStream.errorCallback = () => {
      try {
        Api.Session.get()
      } catch (error) {
        if (error.message === 'Unauthorized') {
          WindowLocation.reload()
        }
      }
    }

    $(document).ajaxError((_event, xhr) => {
      if (xhr.status >= 500) {
        setServerError('NexiaStream failed')
      } else if (xhr.status === 401) {
        WindowLocation.reload()
      }
    })
  }, [setServerError]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSessionLoaded) { return }

    GA4.skipGoogleAnalytics(session.isAdmin)

    GA4.setUserProperties(session.userId, session.roles, session.dealerId)

    Apptentive.createConversation(session)

    if (!_.include(['as', 'trane'], session.companyBrand)) {
      Honeybadger.notify('Unrecognized companyBrand', { context: { session } })
    }

    AmplifyLoginHelper.handleRedirect()

    const eventData = { category: 'Branding', action: redirectCalled ? 'redirectLink' : 'directLink', label: session.companyBrand }

    GA4.sendEvent(eventData)
  }, [isSessionLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    isAdmin,
    isDealer,
    isDealerAdmin,
    isDeveloper,
    isFsr,
    isTechnician,
    isUnknown
  } = useMemo(() => session || {}, [session])
  const isAdminOrDealer = isDealer || isAdmin
  const isAnyRoleExceptDealer = isAdmin || isDeveloper || isFsr || isTechnician

  if (!isSessionLoaded) { return null }

  return (
    <FeaturesContextProvider>
      <StoreContextProvider>
        <S.ScrollableContent id="scrollable-content">
          <Suspense fallback={<Spinner variant="page" />}>
            {
            (!session.termsUpToDate || isUnknown)
              ? (
                <>{!session.termsUpToDate ? <TermsAndConditions /> : <YouDontHavePermission />}</>
              )
              : (
                <Routes>
                  <Route path="/" index element={<Navigate to={DEFAULT_PAGE[session.currentRole]} />} />
                  <Route path="site_rebranded" element={<SiteRebranded />} />
                  <Route path="email_subscription_preferences" element={<EmailSubscriptionPreferences />} />
                  <Route path="verify_notification_recipient_email" element={<VerifyNotificationRecipient />} />
                  <Route path="unsubscribe_email" element={<UnsubscribeEmail />} />
                  {isDealerAdmin && <Route path="paywall/*" element={<PaywallRouter />} />}
                  {isDealerAdmin && <Route path="ehmkt/*" element={<HEHMRouter />} />}
                  <Route element={<AppLayout />}>
                    <Route path="/dashboard/paywall/failure" element={<Dashboard hasPaywallError />} />
                    <Route path="/dashboard/paywall/accept" element={<Dashboard hasPaywallAcceptance />} />
                    <Route path="/dashboard/ehmkt/failure" element={<Dashboard hasHEHMError />} />
                    <Route path="/dashboard/ehmkt/accept" element={<Dashboard hasHEHMAcceptance />} />
                    <Route path="/dashboard/unknown_paywall_error" element={<Dashboard hasUnknownPaywallError />} />
                    <Route path="alerts/*" element={<AlertsRouter />} />
                    <Route path="resources/:tab" element={<Resources />} />
                    <Route path="resources" element={<Resources />} />
                    <Route path="terms_and_conditions" element={<TermsAndConditionsForm />} />
                    <Route path="updates/:tab" element={<Updates />} />
                    <Route path="updates" element={<Updates />} />
                    <Route path="warranty_lookup" element={<WarrantyLookup />} />
                    <Route path="dealer_image" element={<DealerImage />} />

                    {!isDealer && <Route path="/favorites/*" element={<FavoritesRouter />} />}
                    {!isDealer && <Route path="/devices/*" element={<DevicesRouter />} />}

                    {isAdminOrDealer && <Route path="archived_devices" element={<ArchivedDevices />} />}
                    {isAdminOrDealer && <Route path="customers/*" element={<CustomersRouter />} />}
                    {isAdminOrDealer && <Route path="dashboard" element={<Dashboard />} />}
                    {isAdminOrDealer && <Route path="legal" element={<Legal />} />}
                    {isAdminOrDealer && <Route path="notifications/*" element={<NotificationsRouter />} />}
                    {isAdminOrDealer && <Route path="unassigned_devices" element={<UnassignedDevices />} />}
                    {isAdminOrDealer && <Route path="unassigned/:deviceId" element={<UnassignedDevice />} />}

                    {isAdmin && <Route path="admin/*" element={<AdminRouter />} />}

                    {isAnyRoleExceptDealer && <Route path="device_aggregator" element={<DeviceAggregator />} />}

                    <Route path="tech_assistant" element={<TechAssistantChatbot />} />
                    {isDealer && <Route path="daily_digest" element={<DailyDigest />} />}

                    <Route path="*" index element={<NotFound />} />
                  </Route>
                </Routes>
              )
          }
          </Suspense>
        </S.ScrollableContent>
        {!isAdmin && <ZendeskWidget />}
      </StoreContextProvider>
    </FeaturesContextProvider>
  )
}

export default AppRouter

const S = {
  ScrollableContent: styled.div`
    height: 100%;
    width: 100%;
  `
}
